import {PlusIcon, KebabHorizontalIcon, ChevronDownIcon, ChevronRightIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, CounterLabel, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import type {ActionConfig, ActionType} from '../config-parser'
import {ALL_ACTION_TYPES, ACTION_METADATA} from '../config-parser'

import styles from './ConfigSections.module.css'
import {TagInput} from './TagInput'

type ActionsSectionProps = {
  actions: ActionConfig[]
  onChange: (actions: ActionConfig[]) => void
}

export function ActionsSection({actions, onChange}: ActionsSectionProps) {
  const [expandedAction, setExpandedAction] = useState<ActionType | null>(
    () => actions.find(a => (a.allowed && a.allowed.length > 0) || (a.blocked && a.blocked.length > 0))?.type ?? null,
  )

  const addAction = useCallback(
    (type: ActionType) => {
      if (!actions.some(a => a.type === type)) {
        onChange([...actions, {type}])
      }
    },
    [actions, onChange],
  )

  const removeAction = useCallback(
    (type: ActionType) => {
      onChange(actions.filter(a => a.type !== type))
      setExpandedAction(prev => (prev === type ? null : prev))
    },
    [actions, onChange],
  )

  const updateAction = useCallback(
    (type: ActionType, updates: Partial<ActionConfig>) => {
      onChange(actions.map(a => (a.type === type ? {...a, ...updates} : a)))
    },
    [actions, onChange],
  )

  const toggleAdvanced = useCallback((type: ActionType) => {
    setExpandedAction(prev => (prev === type ? null : type))
  }, [])

  const availableActions = ALL_ACTION_TYPES.filter(type => !actions.some(a => a.type === type))

  // Total count includes "Post comment" which is always present
  const totalCount = actions.length + 1

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeaderRow}>
        <div className={styles.sectionHeaderText}>
          <h3 className={styles.sectionTitle}>
            Actions <CounterLabel>{totalCount}</CounterLabel>
          </h3>
          <p className={styles.sectionSubtitle}>Configure the actions the agent can take.</p>
        </div>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={PlusIcon} aria-label="Add action" variant="invisible" size="small" />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end">
            <ActionList>
              {availableActions.length > 0 ? (
                availableActions.map(type => {
                  const meta = ACTION_METADATA[type]
                  return (
                    <ActionList.Item key={type} onSelect={() => addAction(type)}>
                      {meta.label}
                      <ActionList.Description variant="block">{meta.description}</ActionList.Description>
                    </ActionList.Item>
                  )
                })
              ) : (
                <ActionList.Item disabled className={styles.emptyState}>
                  All actions have been added
                </ActionList.Item>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>

      <div className={styles.cardList}>
        {/* Post comment — always present, not removable */}
        <div className={styles.actionCardRow} data-testid="action-post-comment">
          <div className={clsx(styles.cardContent, styles.cardContentInline)}>
            <p className={styles.cardTitle}>Post comment</p>
            <p className={styles.cardDescription}>Post comments on issues</p>
          </div>
        </div>

        {/* Configurable actions */}
        {actions.map(action => {
          const meta = ACTION_METADATA[action.type]
          const isExpanded = expandedAction === action.type

          return (
            <div key={action.type} className={styles.actionCardRow} data-testid={`action-${action.type}`}>
              <div className={clsx(styles.cardContent, styles.cardContentInline)}>
                <p className={styles.cardTitle}>{meta.label}</p>
                <p className={styles.cardDescription}>{meta.description}</p>
              </div>
              <div className={styles.cardActions}>
                <Button
                  variant="invisible"
                  size="small"
                  onClick={() => toggleAdvanced(action.type)}
                  aria-expanded={isExpanded}
                  data-testid={`action-advanced-${action.type}`}
                  trailingVisual={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                >
                  Advanced
                </Button>
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton
                      icon={KebabHorizontalIcon}
                      aria-label="Action options"
                      variant="invisible"
                      size="small"
                    />
                  </ActionMenu.Anchor>
                  <ActionMenu.Overlay>
                    <ActionList>
                      <ActionList.Item variant="danger" onSelect={() => removeAction(action.type)}>
                        <ActionList.LeadingVisual>
                          <TrashIcon />
                        </ActionList.LeadingVisual>
                        Remove
                      </ActionList.Item>
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              </div>
              <div
                className={`${styles.advancedContent} ${isExpanded ? styles.advancedContentOpen : styles.advancedContentClosed}`}
              >
                <div className={styles.advancedContentInner}>
                  {meta.allowedLabel && (
                    <div>
                      <p className={styles.fieldLabel}>{meta.allowedLabel}</p>
                      <TagInput
                        values={action.allowed ?? []}
                        onChange={values => updateAction(action.type, {allowed: values})}
                        variant="allowed"
                        aria-label={meta.allowedLabel}
                      />
                    </div>
                  )}
                  {meta.blockedLabel && (
                    <div>
                      <p className={styles.fieldLabel}>{meta.blockedLabel}</p>
                      <TagInput
                        values={action.blocked ?? []}
                        onChange={values => updateAction(action.type, {blocked: values})}
                        variant="blocked"
                        aria-label={meta.blockedLabel}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
